import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import customerApi from '@/lib/marketplaceCustomerApi';

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const emptyEnquiryForm = {
  buyer_name: '',
  buyer_email: '',
  buyer_phone: '',
  quantity: 1,
  address_line_1: '',
  address_line_2: '',
  city: '',
  state: '',
  pin_code: '',
  note: '',
};

const formatPrice = (value) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

export default function MarketplaceEnquiryModal({ product, onClose }) {
  const [buyer, setBuyer] = useState(null);
  const [buyerLoading, setBuyerLoading] = useState(true);
  useEffect(() => {
    customerApi.get('/me').then(({ data }) => setBuyer(data)).catch(() => setBuyer(null)).finally(() => setBuyerLoading(false));
  }, []);
  const [form, setForm] = useState(emptyEnquiryForm);
  const [understandsRequestOnly, setUnderstandsRequestOnly] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!buyer) return;
    setForm((current) => ({
      ...current,
      buyer_name: buyer.name || '',
      buyer_email: buyer.email || '',
      buyer_phone: buyer.phone || '',
    }));
  }, [buyer]);

  if (!product) return null;

  const updateForm = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const submitEnquiry = async (event) => {
    event.preventDefault();
    if (!product?.id || !understandsRequestOnly) return;
    setSubmitting(true);
    setMessage('');
    setError('');
    const shippingAddress = [
      form.address_line_1,
      form.address_line_2,
      form.city,
      form.state,
      form.pin_code ? `PIN: ${form.pin_code}` : '',
    ]
      .filter(Boolean)
      .join(', ');
    try {
      await axios.post(
        `${API}/marketplace/orders`,
        {
          ...form,
          buyer_name: buyer?.name || form.buyer_name,
          buyer_email: buyer?.email || form.buyer_email,
          buyer_phone: form.buyer_phone || buyer?.phone || '',
          product_id: product.id,
          quantity: Number(form.quantity || 1),
          shipping_address: shippingAddress,
        },
        { withCredentials: true }
      );
      setMessage(
        'Request received. KalaManch admin will verify buyer details, availability, payment method, and delivery next steps within 24 hours. No payment has been taken.'
      );
      setForm({
        ...emptyEnquiryForm,
        buyer_name: buyer?.name || '',
        buyer_email: buyer?.email || '',
        buyer_phone: buyer?.phone || '',
      });
      setUnderstandsRequestOnly(false);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not send enquiry right now. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderBuyerGate = () => (
    <div className="mt-6 rounded-3xl border border-purple-100 bg-slate-50 p-5">
      <h3 className="text-xl font-bold">Marketplace customer account</h3>
      <p className="mt-2 text-slate-600">Sign in or sign up on the dedicated marketplace page to continue with this product.</p>
      <Link className="btn-primary mt-4 inline-block" to={`/marketplace/account?returnTo=${encodeURIComponent(`/marketplace/product/${product.id}`)}`}>Customer sign in / sign up</Link>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[90] overflow-y-auto bg-slate-950/60 px-4 py-8">
      <div className="mx-auto max-w-3xl rounded-[1.5rem] bg-white p-5 shadow-2xl sm:p-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.2em] text-[#4A148C]">
              Request availability
            </p>
            <h2 className="mt-2 text-3xl font-black text-slate-950">
              {product.title}
            </h2>
            <p className="mt-1 font-bold text-[#C45A00]">
              {formatPrice(product.price_inr)}
            </p>
            <div className="mt-4 rounded-2xl border border-orange-100 bg-orange-50 px-4 py-3 text-sm font-bold leading-6 text-[#9A4300]">
              This is not instant checkout and no payment is collected here. KalaManch admin confirms availability, payment, and delivery before anything is final.
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-slate-200 px-4 py-2 text-sm font-black text-slate-700"
          >
            Close
          </button>
        </div>

        {buyerLoading ? (
          <div className="mt-6 rounded-2xl bg-slate-50 px-4 py-5 text-sm font-bold text-slate-600">
            Checking customer login...
          </div>
        ) : !buyer ? (
          renderBuyerGate()
        ) : message ? (
          <div className="mt-6 rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-4 text-sm font-bold leading-6 text-emerald-700">
            {message}
          </div>
        ) : (
          <form className="mt-6 grid gap-4" onSubmit={submitEnquiry}>
            <div className="rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm font-bold text-emerald-800">
              Enquiry will be sent as {buyer.name}. Buyer contact and delivery details remain visible only to KalaManch admin.
            </div>
            <label>
              <span className="text-sm font-black text-slate-800">Quantity *</span>
              <input
                required
                min="1"
                type="number"
                value={form.quantity}
                onChange={(event) => updateForm('quantity', event.target.value)}
                className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 outline-none focus:border-[#FF6F00]"
                aria-label="Quantity required"
              />
            </label>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-sm font-black text-slate-900">
                {product.product_type === 'digital' ? 'Billing / delivery details *' : 'Delivery address *'}
              </p>
              {product.product_type === 'digital' && (
                <p className="mt-1 text-xs font-bold leading-5 text-slate-500">
                  For digital products, these details help KalaManch verify the buyer and share invoice/support information.
                </p>
              )}
              <div className="mt-3 grid gap-4 md:grid-cols-2">
                <input
                  required
                  value={form.address_line_1}
                  onChange={(event) => updateForm('address_line_1', event.target.value)}
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-3 outline-none focus:border-[#FF6F00]"
                  placeholder="Address line 1 *"
                />
                <input
                  required
                  value={form.address_line_2}
                  onChange={(event) => updateForm('address_line_2', event.target.value)}
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-3 outline-none focus:border-[#FF6F00]"
                  placeholder="Address line 2 *"
                />
                <input
                  required
                  value={form.city}
                  onChange={(event) => updateForm('city', event.target.value)}
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-3 outline-none focus:border-[#FF6F00]"
                  placeholder="City *"
                />
                <input
                  required
                  value={form.state}
                  onChange={(event) => updateForm('state', event.target.value)}
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-3 outline-none focus:border-[#FF6F00]"
                  placeholder="State *"
                />
                <input
                  required
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  value={form.pin_code}
                  onChange={(event) => updateForm('pin_code', event.target.value)}
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-3 outline-none focus:border-[#FF6F00]"
                  placeholder="PIN code *"
                />
              </div>
            </div>
            <label>
              <span className="text-sm font-black text-slate-800">Message for KalaManch admin *</span>
              <textarea
                required
                value={form.note}
                onChange={(event) => updateForm('note', event.target.value)}
                className="mt-2 min-h-[5rem] w-full rounded-2xl border border-slate-200 px-4 py-3 outline-none focus:border-[#FF6F00]"
                placeholder="Questions, customization request, delivery note, or preferred callback time"
              />
            </label>
            <label className="flex gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold leading-6 text-slate-700">
              <input
                type="checkbox"
                checked={understandsRequestOnly}
                onChange={(event) => setUnderstandsRequestOnly(event.target.checked)}
                className="mt-1 h-4 w-4"
              />
              <span>
                I understand this is only an enquiry/reservation request. KalaManch will confirm availability and payment steps before any order is final.
              </span>
            </label>
            {error && (
              <div className="rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-bold text-red-700">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={submitting || !understandsRequestOnly}
              className="rounded-full bg-[#FF6F00] px-6 py-3 text-sm font-black text-white disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? 'Sending...' : 'Send request to KalaManch admin'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
